import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { DashboardMode } from './useDashboardMode'

export interface DriverProfile {
  preferredEnergyUnit: 'kwh' | 'wh_per_km'
  visitsByMode: Record<DashboardMode, number>
  lastSeenAt: string | null
  preferredDefaultMode: DashboardMode | null
  pinnedCards: string[]
  hiddenCards: string[]
  themeAccent: 'neon' | 'classic' | 'minimal'
  cardViews: Record<string, number>
}

const STORAGE_KEY = 'driver-profile-v1'

const DEFAULT_VISITS: Record<DashboardMode, number> = { driving: 0, parked: 0, charging: 0, analytics: 0, 'trip-planning': 0 }

const DEFAULT_PROFILE: DriverProfile = {
  preferredEnergyUnit: 'kwh',
  visitsByMode: { ...DEFAULT_VISITS },
  lastSeenAt: null,
  preferredDefaultMode: null,
  pinnedCards: [],
  hiddenCards: [],
  themeAccent: 'classic',
  cardViews: {},
}

function readStoredProfile(): DriverProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_PROFILE, visitsByMode: { ...DEFAULT_VISITS } }
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_PROFILE, visitsByMode: { ...DEFAULT_VISITS } }
    const p = parsed as Record<string, unknown>
    if (
      (p['preferredEnergyUnit'] !== 'kwh' && p['preferredEnergyUnit'] !== 'wh_per_km') ||
      typeof p['visitsByMode'] !== 'object' ||
      p['visitsByMode'] === null
    ) {
      return { ...DEFAULT_PROFILE, visitsByMode: { ...DEFAULT_VISITS } }
    }
    const pdm = p['preferredDefaultMode']
    const preferredDefaultMode: DashboardMode | null =
      pdm === 'driving' || pdm === 'parked' || pdm === 'charging' || pdm === 'analytics' || pdm === 'trip-planning' ? pdm : null
    const ta = p['themeAccent']
    const themeAccent: 'neon' | 'classic' | 'minimal' =
      ta === 'neon' || ta === 'minimal' ? ta : 'classic'
    return {
      ...(parsed as DriverProfile),
      preferredDefaultMode,
      themeAccent,
      pinnedCards: Array.isArray(p['pinnedCards']) ? (p['pinnedCards'] as string[]) : [],
      hiddenCards: Array.isArray(p['hiddenCards']) ? (p['hiddenCards'] as string[]) : [],
      cardViews: (typeof p['cardViews'] === 'object' && p['cardViews'] !== null && !Array.isArray(p['cardViews'])) ? (p['cardViews'] as Record<string, number>) : {},
    }
  } catch {
    return { ...DEFAULT_PROFILE, visitsByMode: { ...DEFAULT_VISITS } }
  }
}

// Module-level singleton so all callers share the same reactive state
export const profile: Ref<DriverProfile> = ref<DriverProfile>(readStoredProfile())

const MODES = ['driving', 'parked', 'charging', 'analytics', 'trip-planning'] as const

export const mostVisitedMode: ComputedRef<DashboardMode | null> = computed(() => {
  const visits = profile.value.visitsByMode
  let best: DashboardMode | null = null
  let bestCount = 0
  for (const mode of MODES) {
    const v = visits[mode] ?? 0
    if (v > bestCount) { best = mode; bestCount = v }
  }
  return bestCount >= 5 ? best : null
})

function persist(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile.value))
}

export const mostViewedUnpinnedCard: ComputedRef<string | null> = computed(() => {
  const views = profile.value.cardViews
  const pinned = profile.value.pinnedCards
  const hidden = profile.value.hiddenCards
  let best: string | null = null
  let bestCount = 0
  for (const [id, count] of Object.entries(views)) {
    if (count > bestCount && !pinned.includes(id) && !hidden.includes(id)) {
      best = id
      bestCount = count
    }
  }
  return bestCount >= 5 ? best : null
})

export function recordCardView(id: string): void {
  const views = { ...profile.value.cardViews }
  views[id] = (views[id] ?? 0) + 1
  profile.value = { ...profile.value, cardViews: views }
  persist()
}

export function useDriverProfile(): {
  profile: Ref<DriverProfile>
  mostVisitedMode: ComputedRef<DashboardMode | null>
  mostViewedUnpinnedCard: ComputedRef<string | null>
  updateProfile: (patch: Partial<DriverProfile>) => void
  setProfile: (patch: Partial<DriverProfile>) => void
  recordModeVisit: (mode: DashboardMode) => void
  recordCardView: (id: string) => void
  resetProfile: () => void
} {
  function updateProfile(patch: Partial<DriverProfile>): void {
    profile.value = { ...profile.value, ...patch }
    persist()
  }

  const setProfile = updateProfile

  function recordModeVisit(mode: DashboardMode): void {
    const visits = { ...profile.value.visitsByMode }
    visits[mode] = (visits[mode] ?? 0) + 1
    profile.value = { ...profile.value, visitsByMode: visits, lastSeenAt: new Date().toISOString() }
    persist()
  }

  function resetProfile(): void {
    profile.value = { ...DEFAULT_PROFILE, visitsByMode: { ...DEFAULT_VISITS } }
    localStorage.removeItem(STORAGE_KEY)
  }

  return { profile, mostVisitedMode, mostViewedUnpinnedCard, updateProfile, setProfile, recordModeVisit, recordCardView, resetProfile }
}
